/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getQueryClient } from '@opengeoweb/api';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import type { InitialProps } from '@opengeoweb/core';
import { usePluginCatalog } from '@opengeoweb/plugins';
import {
  AlertBanner,
  Filter,
  LastUpdateTime,
  SearchField,
} from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import {
  mapSelectors,
  syncGroupsSelectors,
  uiSelectors,
  uiTypes,
} from '@opengeoweb/store';
import type { TimeSeriesModuleState } from '@opengeoweb/time-series';
import { timeSeriesSelectors } from '@opengeoweb/time-series';
import type { WarningModuleStore } from '@opengeoweb/warnings';
import { publicWarningsSelectors } from '@opengeoweb/warnings';
import { updateViewPreset } from '../../api/viewpreset-api/api';
import { useViewPresets } from '../../api/viewpreset-api/hooks';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import type { AppStore } from '../../store/types';
import {
  viewPresetActions,
  viewPresetSelectors,
} from '../../store/viewPresets';
import type {
  ViewPreset,
  ViewPresetDialog,
  ViewPresetFormValues,
  ViewPresetsListFilter,
} from '../../store/viewPresets/types';
import {
  PresetAction,
  ViewPresetErrorType,
} from '../../store/viewPresets/types';
import {
  getViewComponentType,
  getViewById,
} from '../../store/workspace/selectors';
import type {
  ViewPresetListItem,
  WorkspacePresetLabels,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import type { PresetFilterType } from '../../store/workspaceList/types';
import { getEmptyWorkspaceListItem } from '../../store/workspaceList/utils';
import {
  sanitizeViewPresetForClipboard,
  writeToClipboard,
} from '../../utils/clipboardUtils';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { filterAndSortPresets } from '../../utils/utils';
import WorkspaceMenuLabelsPanel from '../WorkspaceSelectList/WorkspaceMenuLabelsWrapper';
import WorkspacePresetInfo from '../WorkspaceSelectList/WorkspacePresetInfo';
import WorkspaceSelectList from '../WorkspaceSelectList/WorkspaceSelectList';

interface ViewPresetMenuListConnectProps {
  panelId: string;
  isLabelFilterOpen?: boolean;
  onToggleLabelFilter?: () => void;
  hasSpaceForLabelFilter?: boolean;
}

export const ViewPresetMenuListConnect: React.FC<
  ViewPresetMenuListConnectProps
> = ({
  panelId,
  isLabelFilterOpen = false,
  onToggleLabelFilter,
  hasSpaceForLabelFilter = true,
}: ViewPresetMenuListConnectProps) => {
  const { t } = useWorkspaceTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const store = useStore<AppStore>();
  const { currentRole, isLoggedIn, auth } = useAuthenticationContext();
  const username = auth?.username || '';
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const scope = !isAdmin ? 'user' : 'system';
  const { data: pluginCatalog } = usePluginCatalog();

  const pluginCatalogAsPresets: ViewPresetListItem[] = React.useMemo(
    () =>
      Object.entries(pluginCatalog ?? {})
        .filter(([, plugin]) => plugin.type !== 'overlay') // overlay plugin will have its own menu.
        .map(
          ([id, plugin]) =>
            ({
              id,
              title: plugin.name,
              description: plugin.description,
              scope: 'plugin',
              username: plugin.author?.name,
              labels: { viewType: ['Plugin'] },

              origin: {
                type: 'plugin',
                pluginId: id,
                versions: plugin.versions,
              },

              date: plugin.created,
              updated: plugin.modified,
            }) as ViewPresetListItem,
        ),
    [pluginCatalog],
  );

  const { data: viewPresets, isFetching: isViewPresetsListFetching } =
    useViewPresets();

  const viewPresetListFilters = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetListFiltersForView(
      store,
      panelId,
      scope,
      isAdmin,
    ),
  );

  const viewPresetListSearchQuery = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetListSearchQueryForView(store, panelId),
  );

  const viewPresetLabelFilters = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetLabelFilters(store, panelId),
  );

  const hasActiveLabelFilters = React.useMemo(() => {
    if (!viewPresetLabelFilters) {
      return false;
    }

    return Object.values(viewPresetLabelFilters).some(
      (group) => Array.isArray(group) && group.length > 0,
    );
  }, [viewPresetLabelFilters]);

  const activeLabelFilterCount = React.useMemo(() => {
    if (!viewPresetLabelFilters) {
      return 0;
    }

    return Object.values(viewPresetLabelFilters).flat().length;
  }, [viewPresetLabelFilters]);

  const filteredPresets = React.useMemo(() => {
    if (viewPresets) {
      return [
        ...filterAndSortPresets(
          [...viewPresets, ...pluginCatalogAsPresets],
          viewPresetListFilters,
          username,
          isAdmin,
          viewPresetLabelFilters ?? undefined,
          viewPresetListSearchQuery,
        ),
      ];
    }
    return [];
  }, [
    viewPresets,
    viewPresetListFilters,
    username,
    isAdmin,
    viewPresetLabelFilters,
    viewPresetListSearchQuery,
    pluginCatalogAsPresets,
  ]);

  const labelsFormMethods = useForm<ViewPresetFormValues>({
    defaultValues: {
      labels: viewPresetLabelFilters,
    },
  });

  const infoPanelAnchorRef = React.useRef<HTMLDivElement | null>(null);

  const [infoPreset, setInfoPreset] = React.useState<WorkspacePresetListItem>();
  React.useEffect(() => {
    if (hasActiveLabelFilters && !hasSpaceForLabelFilter) {
      labelsFormMethods.reset({ labels: undefined });
      dispatch(
        viewPresetActions.clearViewPresetLabelFilters({
          panelId,
        }),
      );
    }
  }, [
    dispatch,
    hasActiveLabelFilters,
    isLabelFilterOpen,
    hasSpaceForLabelFilter,
    labelsFormMethods,
    panelId,
  ]);

  const activeViewPresetId = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetActiveId(store, panelId),
  );

  const viewComponentType = useSelector((store: AppStore) =>
    getViewComponentType(store, panelId),
  );

  const currentMapPreset = useSelector((store: AppStore) =>
    mapSelectors.getMapPreset(store, panelId),
  );
  const currentTimeSeriesPreset = useSelector(
    (store: AppStore & TimeSeriesModuleState) =>
      timeSeriesSelectors.getTimeSeriesPreset(store, panelId),
  );
  const currentWarningsPreset = useSelector((store: WarningModuleStore) =>
    publicWarningsSelectors.getWarningsPresetById(store, panelId),
  );

  const relatedSyncGroupIds = useSelector((store: AppStore) =>
    syncGroupsSelectors.getAllTargetGroupsForSource(store, {
      sourceId: panelId,
    }),
  );
  const error = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetsError(store, panelId),
  );

  const openViewPresetsDialog = React.useCallback(
    (viewPresetDialogOptions: ViewPresetDialog) => {
      dispatch(
        viewPresetActions.openViewPresetDialog({
          viewPresetDialog: viewPresetDialogOptions,
        }),
      );
    },
    [dispatch],
  );

  const onSelectVersion = React.useCallback(
    (presetId: string, version: string) => {
      const plugin = pluginCatalog?.[presetId];
      if (plugin) {
        dispatch(
          viewPresetActions.selectViewPlugin({
            panelId,
            pluginMetaData: { ...plugin, version },
          }),
        );
      }
    },
    [dispatch, panelId, pluginCatalog],
  );

  const selectViewPreset = React.useCallback(
    async (viewPresetId: string) => {
      const plugin = pluginCatalog?.[viewPresetId];
      // only on loading a plugin view from outside geoweb, not when selecting a regular preset
      if (plugin) {
        dispatch(
          viewPresetActions.selectViewPlugin({
            panelId,
            pluginMetaData: plugin,
          }),
        );
      } else {
        dispatch(
          viewPresetActions.selectViewPreset({
            panelId,
            viewPresetId,
          }),
        );
      }
    },
    [dispatch, panelId, pluginCatalog],
  );

  const refreshViewPresets = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: viewpresetQueryOptions.keys.lists(),
    });
  }, [queryClient]);

  const constructInitialProps = (): InitialProps | undefined => {
    switch (viewComponentType) {
      case 'WarningList':
      case 'WarningsTimeline':
        return currentWarningsPreset;
      case 'TimeSeries': {
        const state = store.getState();
        const isLocationsDialogOpen =
          uiSelectors.getisDialogOpen(
            state,
            uiTypes.DialogTypes.TimeseriesLocations,
          ) &&
          uiSelectors.getSourcePanelId(
            state,
            uiTypes.DialogTypes.TimeseriesLocations,
          ) === panelId;
        return { ...currentTimeSeriesPreset, isLocationsDialogOpen };
      }
      case 'Map':
        return {
          mapPreset: currentMapPreset,
          syncGroupsIds: relatedSyncGroupIds,
        };
      default:
        return undefined;
    }
  };

  const onClickViewPresetOption = async (
    presetId: string,
    workspaceAction: WorkspacePresetAction,
    preset: WorkspacePresetListItem,
  ): Promise<void> => {
    switch (workspaceAction) {
      case WorkspacePresetAction.DUPLICATE: {
        getQueryClient()
          .fetchQuery(viewpresetQueryOptions.getViewPreset(presetId, auth!))
          .then((presetFromBE) => {
            const preset = presetFromBE as unknown as ViewPreset;
            const fallbackInitialProps = getViewById(store.getState(), panelId)
              ?.initialProps as InitialProps | undefined;
            const initialPropsForForm =
              preset.initialProps ??
              fallbackInitialProps ??
              constructInitialProps();

            openViewPresetsDialog({
              title: t('workspace-mappreset-save-as'),
              action: PresetAction.SAVE_AS,
              viewPresetId: presetId,
              panelId,
              shouldSetActiveViewPreset: false,
              formValues: {
                title: preset.title,
                description: preset.description,
                initialProps: initialPropsForForm,
                abstract: preset.abstract,
                labels: preset.labels,
                componentType: preset.componentType,
              },
            });
          })
          .catch(() => {
            openViewPresetsDialog({
              title: t('workspace-mappreset-save-as'),
              action: PresetAction.SAVE_AS,
              viewPresetId: presetId,
              panelId,
              shouldSetActiveViewPreset: false,
              formValues: {
                title: preset.title,
                description: preset.description,
                initialProps: constructInitialProps(),
                abstract: preset.abstract,
                labels: preset.labels,
                componentType: viewComponentType,
              },
            });
          });
        break;
      }
      case WorkspacePresetAction.EDIT: {
        const presetTitle = preset?.title || '';
        openViewPresetsDialog({
          title: t('workspace-mappreset-dialog-title-edit'),
          action: PresetAction.EDIT,
          panelId,
          viewPresetId: presetId!,
          formValues: {
            title: presetTitle,
            description: preset.description,
            abstract: preset.abstract,
            labels: preset.labels,
            updated: new Date().toISOString(),
          },
        });

        void getQueryClient()
          .fetchQuery(viewpresetQueryOptions.getViewPreset(presetId, auth!))
          .then((fullPreset) => {
            openViewPresetsDialog({
              title: t('workspace-mappreset-dialog-title-edit'),
              action: PresetAction.EDIT,
              panelId,
              viewPresetId: presetId!,
              formValues: {
                title: fullPreset.title,
                description: fullPreset.description,
                abstract: fullPreset.abstract,
                labels: fullPreset.labels,
                componentType: (fullPreset as unknown as ViewPreset)
                  .componentType,
                initialProps: (fullPreset as unknown as ViewPreset)
                  .initialProps,
              },
            });
          })
          .catch(() => {
            // Leave dialog with initial data if fetch fails; errors handled elsewhere
          });
        break;
      }
      case WorkspacePresetAction.SHARE: {
        void getQueryClient()
          .fetchQuery(viewpresetQueryOptions.getViewPreset(presetId, auth!))
          .then(
            (preset: ViewPreset) =>
              void updateViewPreset(
                presetId!,
                { ...preset, is_shared: !preset.is_shared },
                auth!,
              ).then(() => {
                dispatch(
                  viewPresetActions.onSuccessViewPresetAction({
                    panelId,
                    viewPresetId: presetId,
                    activeViewPresetId,
                    action: PresetAction.SHARE,
                    title: preset.title,
                    description: preset.description,
                    scope,
                    abstract: preset.abstract,
                    is_shared: preset.is_shared,
                  }),
                );
              }),
          );
        break;
      }
      case WorkspacePresetAction.DELETE_SYSTEM_PRESET:
      case WorkspacePresetAction.DELETE: {
        openViewPresetsDialog({
          title: t('workspace-mappreset-dialog-title-delete'),
          action: PresetAction.DELETE,
          viewPresetId: presetId,
          panelId,
          formValues: { title: preset.title },
        });
        break;
      }
      case WorkspacePresetAction.COPY_TO_CLIPBOARD: {
        try {
          const fullPreset = await getQueryClient().fetchQuery(
            viewpresetQueryOptions.getViewPreset(presetId, auth!),
          );
          const sanitized = sanitizeViewPresetForClipboard(
            fullPreset as unknown as ViewPreset,
          );
          await writeToClipboard(JSON.stringify(sanitized, null, 2));
          dispatch(
            snackbarActions.openSnackbar({
              type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
              message: t('workspace-copy-viewpreset-success'),
            }),
          );
        } catch {
          dispatch(
            snackbarActions.openSnackbar({
              type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
              message: t('workspace-error-generic'),
            }),
          );
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const onSearchFilter = React.useCallback(
    (searchQuery: string) => {
      dispatch(
        viewPresetActions.searchFilter({
          searchQuery,
          panelId,
        }),
      );
    },
    [dispatch, panelId],
  );

  const toggleSelectFilterChip = React.useCallback(
    (id: PresetFilterType, isSelected: boolean) => {
      dispatch(
        viewPresetActions.toggleSelectFilterChip({
          id,
          isSelected,
          panelId,
          isAdmin,
        }),
      );
    },
    [dispatch, panelId, isAdmin],
  );

  const setSelectAllFilterChip = React.useCallback(() => {
    dispatch(viewPresetActions.setSelectAllFilterChip({ panelId }));
  }, [dispatch, panelId]);

  const onClickFilterChip = (id: string, selected: boolean): void => {
    if (id === 'all') {
      if (selected) {
        setSelectAllFilterChip();
      }
    } else {
      toggleSelectFilterChip(id as PresetFilterType, selected);
    }
  };

  const onLabelsFilterChange = React.useCallback(
    (labels: WorkspacePresetLabels) => {
      dispatch(
        viewPresetActions.setViewPresetLabelFilters({
          labels,
          panelId,
        }),
      );
    },
    [dispatch, panelId],
  );

  const isAllSelectedForFilters = viewPresetListFilters.every(
    (filter: ViewPresetsListFilter) => filter.isSelected,
  );

  const showLabelFilterToggle = Boolean(onToggleLabelFilter);

  const shouldHideNewWorkspace =
    !isAllSelectedForFilters ||
    viewPresetListSearchQuery.length > 0 ||
    hasActiveLabelFilters;

  const closeInfo = React.useCallback((): void => {
    setInfoPreset(undefined);
  }, []);

  const onOpenPresetInfo = React.useCallback(
    (preset: WorkspacePresetListItem): void => {
      setInfoPreset((current) =>
        current?.id === preset.id ? undefined : preset,
      );
    },
    [],
  );

  React.useEffect(() => {
    if (
      infoPreset &&
      !filteredPresets.some((preset) => preset.id === infoPreset.id)
    ) {
      closeInfo();
    }
  }, [infoPreset, filteredPresets, closeInfo]);

  return (
    <Box
      data-testid="viewpreset-selectlist-connect"
      sx={{
        bgcolor: 'geowebColors.background.surface',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          height: 4,
          overflow: 'hidden',
        }}
      >
        {!!isViewPresetsListFetching && (
          <LinearProgress color="secondary" data-testid="loading-bar" />
        )}
      </Box>

      {!isViewPresetsListFetching && !!error && (
        <AlertBanner
          info={error.message}
          shouldClose
          title={t('workspace-viewpresets-error')}
          {...(error.errorType === ViewPresetErrorType.GENERIC && {
            actionButtonProps: {
              title: t('workspace-retry'),
              onClick: refreshViewPresets,
            },
          })}
        />
      )}

      {!error && (
        <SearchField
          searchFilter={viewPresetListSearchQuery}
          setSearchFilter={onSearchFilter}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isLabelFilterOpen ? '280px 1fr' : '1fr',
          flexGrow: 1,
          minHeight: 0,
          padding: 1,
          bgcolor: 'geowebColors.background.surface',
          overflow: 'hidden',
        }}
      >
        {!!isLabelFilterOpen && (
          <Box
            sx={{
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'geowebColors.background.surface',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <FormProvider {...labelsFormMethods}>
              <WorkspaceMenuLabelsPanel
                menuLabels={viewPresetLabelFilters}
                onChange={onLabelsFilterChange}
              />
            </FormProvider>
          </Box>
        )}

        <Box
          ref={infoPanelAnchorRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 1,
            overflow: 'hidden',
          }}
        >
          {!error && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                marginBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Filter
                  activeLabelFilterCount={activeLabelFilterCount}
                  filters={viewPresetListFilters}
                  isAllSelected={isAllSelectedForFilters}
                  isLabelFilterOpen={isLabelFilterOpen}
                  onClickFilterChip={onClickFilterChip}
                  onSearch={onSearchFilter}
                  onToggleLabelFilter={onToggleLabelFilter}
                  searchQuery={viewPresetListSearchQuery}
                  showLabelFilterToggle={showLabelFilterToggle}
                  showSearchField={false}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <LastUpdateTime
                  hideLabel
                  onPressRefresh={async (): Promise<void> =>
                    refreshViewPresets()
                  }
                />
              </Box>
            </Box>
          )}

          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <WorkspaceSelectList
              currentSelectedId={activeViewPresetId || undefined!}
              hideNewWorkspace={shouldHideNewWorkspace}
              infoOpenPresetId={infoPreset?.id}
              isAdmin={isAdmin}
              isEditable={isLoggedIn}
              newPresets={[
                {
                  ...getEmptyWorkspaceListItem(t),
                  title: t('workspace-mappreset-new'),
                },
              ]}
              onClickWorkspacePreset={selectViewPreset}
              onClickWorkspacePresetOption={onClickViewPresetOption}
              onOpenPresetInfo={onOpenPresetInfo}
              onSelectVersion={onSelectVersion}
              searchQuery={viewPresetListSearchQuery}
              testIdPrefix="viewpreset"
              workspacePresets={filteredPresets as WorkspacePresetListItem[]}
            />
            <WorkspacePresetInfo
              anchorEl={infoPanelAnchorRef.current}
              isOpen={Boolean(infoPreset)}
              onClose={closeInfo}
              preset={infoPreset}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
